import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Input,
  Heading,
  VStack,
  Flex,
  Grid,
  GridItem,
  InputGroup,
  InputRightElement,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  useToast,
  FormLabel,
  useColorMode,
  Card,
  CardBody,
  Skeleton,
  IconButton,
  Tooltip,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  InputLeftElement,
  FormControl,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Stack,
  HStack,
  Switch,
} from '@chakra-ui/react';
import { MdSave, MdRefresh, MdVisibility, MdVisibilityOff, MdHelp, MdEdit, MdCancel, MdPerson, MdEmail, MdSecurity, MdSettings } from 'react-icons/md';
import axios from '../utils/axiosConfig';
import { Helmet } from "react-helmet";
import Navigation from '../components/Navigation';
import useCheckAuth from '../hooks/useCheckAuth';
import PhoneNumberManager from '../components/PhoneNumberManager';

const SettingsPage = () => {
  useCheckAuth();
  const { colorMode } = useColorMode();
  const toast = useToast();
  
  const [formState, setFormState] = useState({
    twilioAccountSid: '',
    twilioAuthToken: '',
    openaiApiKey: '',
    deepgramApiKey: '',
    cartesiaApiKey: '',
    voiceDomainName: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
  });
  const [stats, setStats] = useState({
    totalCalls: 0,
    totalMinutes: 0,
    lastLogin: null,
    usageStats: {
      dailyAverage: 0,
      weeklyTotal: 0,
      monthlyTotal: 0,
      successRate: 0
    }
  });

  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showPasswords, setShowPasswords] = useState({
    twilioAccountSid: false,
    twilioAuthToken: false,
    openaiApiKey: false,
    deepgramApiKey: false,
    cartesiaApiKey: false
  });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/protected/settings');
      if (response.data.success) {
        setFormState(response.data.data);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to fetch settings',
          status: 'error',
          duration: 5000,
          isClosable: true,
          variant: 'solid'
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch settings',
        status: 'error',
        duration: 5000,
        isClosable: true,
        variant: 'solid'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put('/api/protected/settings', formState);
      if (response.data.success) {
        toast({
          title: 'Settings Updated',
          description: 'Your settings have been saved successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'solid'
        });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to update settings',
        status: 'error',
        duration: 5000,
        isClosable: true,
        variant: 'solid'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const renderApiKeyInput = (label, name, value, showPassword) => (
    <InputGroup size="md">
      <FormLabel fontSize="sm" color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>
        {label}
      </FormLabel>
      <Input
        type={showPassword ? "text" : "password"}
        placeholder={label}
        name={name}
        value={value || ''}
        onChange={handleInputChange}
        bg={colorMode === 'light' ? 'white' : 'gray.700'}
        color={colorMode === 'light' ? 'gray.800' : 'gray.300'}
        pr="4.5rem"
        _focus={{ borderColor: 'brand.500' }}
      />
      <InputRightElement>
        <IconButton
          icon={showPassword ? <MdVisibilityOff /> : <MdVisibility />}
          aria-label={`Toggle ${label} visibility`}
          size="sm"
          variant="ghost"
          onClick={() => handleTogglePasswordVisibility(name)}
        />
      </InputRightElement>
    </InputGroup>
  );

  const renderSettingsCard = (title, children) => (
    <Card
      bg={colorMode === 'light' ? 'white' : 'gray.700'}
      borderRadius="lg"
      boxShadow="sm"
      mb={4}
    >
      <CardBody>
        <Heading size="md" mb={4} color={colorMode === 'light' ? 'gray.700' : 'white'}>
          {title}
        </Heading>
        {children}
      </CardBody>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>Settings | AI Call System</title>
        <meta name="description" content="Manage your account settings and preferences" />
      </Helmet>

      <Flex minH="100vh" bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}>
        <Navigation />
        <Box flex="1" p={6}>
          <Flex justify="space-between" align="center" mb={6}>
            <Heading 
              size="lg" 
              color={colorMode === 'light' ? 'gray.800' : 'white'}
            >
              Settings
            </Heading>
            <Button
              leftIcon={<MdSave />}
              colorScheme="brand"
              onClick={handleSave}
              isLoading={saving}
              loadingText="Saving..."
            >
              Save Changes
            </Button>
          </Flex>

          {error && (
            <Alert status="error" mb={6} borderRadius="lg">
              <AlertIcon />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Tabs index={activeTab} onChange={setActiveTab} isFitted variant="enclosed">
            <TabList mb={4}>
              <Tab>3rd Party Credentials</Tab>
              <Tab>Your Phone Numbers</Tab>
            </TabList>
            
            <TabPanels>
              <TabPanel>
                {loading ? (
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                    {Array(4).fill(0).map((_, i) => (
                      <Skeleton key={i} height="200px" borderRadius="lg" />
                    ))}
                  </Grid>
                ) : (
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                    {renderSettingsCard('Twilio Settings', (
                      <VStack spacing={4}>
                        {renderApiKeyInput('Twilio Account SID', 'twilioAccountSid', formState.twilioAccountSid, showPasswords.twilioAccountSid)}
                        {renderApiKeyInput('Twilio Auth Token', 'twilioAuthToken', formState.twilioAuthToken, showPasswords.twilioAuthToken)}
                      </VStack>
                    ))}

                    {renderSettingsCard('OpenAI Settings', (
                      renderApiKeyInput('OpenAI API Key', 'openaiApiKey', formState.openaiApiKey, showPasswords.openaiApiKey)
                    ))}

                    {renderSettingsCard('Deepgram Settings', (
                      renderApiKeyInput('Deepgram API Key', 'deepgramApiKey', formState.deepgramApiKey, showPasswords.deepgramApiKey)
                    ))}

                    {renderSettingsCard('Cartesia Settings', (
                      renderApiKeyInput('Cartesia API Key', 'cartesiaApiKey', formState.cartesiaApiKey, showPasswords.cartesiaApiKey)
                    ))}

                    {renderSettingsCard('Domain Settings', (
                      <VStack spacing={4}>
                        <FormControl>
                          <HStack spacing={4} align="center">
                            <FormLabel fontSize="sm" color={colorMode === 'light' ? 'gray.600' : 'gray.100'} mb={0}>
                              Domain Name
                            </FormLabel>
                            <Input
                              placeholder="example.com"
                              name="voiceDomainName"
                              value={formState.voiceDomainName || ''}
                              onChange={handleInputChange}
                              bg={colorMode === 'light' ? 'white' : 'gray.700'}
                              color={colorMode === 'light' ? 'gray.800' : 'gray.300'}
                              // bg={colorMode === 'light' ? 'white' : 'gray.800'}
                              // color={colorMode === 'light' ? 'gray.800' : 'white'}
                              // borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                              // _hover={{
                              //   borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
                              // }}
                              // _focus={{
                              //   borderColor: 'brand.500',
                              //   boxShadow: `0 0 0 1px ${colorMode === 'light' ? '#3182ce' : '#63b3ed'}`
                              // }}
                            />
                          </HStack>
                        </FormControl>
                      </VStack>
                    ))}
                  </Grid>
                )}
              </TabPanel>

              <TabPanel>
                <PhoneNumberManager />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Flex>
    </>
  );
};

export default SettingsPage;
